// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Option } from "@insite/client-framework/Types/FieldDefinition";
import { get } from "@insite/mobius/utilities";
import isArray from "lodash/isArray";
import mergeWith from "lodash/mergeWith";

/**
 * Get all system colors/PreStyleColors
 * @param colors
 */
export const getAllColorsAsDropdownOptions = (colors: { [key: string]: any }): Option<string>[] => {
    let options: Option<string>[] = [];
    const getDeepKeys = (colors: { [key: string]: any }): string[] => {
        let keys: string[] = [];
        for (const key in colors) {
            if (typeof colors[key] !== "object") {
                keys.push(key);
            }
            if (typeof colors[key] === "object") {
                const subKeys = getDeepKeys(colors[key]);
                keys = keys.concat(
                    subKeys.map(subKey => {
                        return `${key}.${subKey}`;
                    }),
                );
            }
        }
        return keys;
    };

    options = getDeepKeys(colors).map(option => ({
        displayName: `${option}`,
        value: `${get(colors, option)}`,
    }));
    return options;
};

/**
 * Merge 2 style objects without full override
 * @param object1
 * @param object2
 */
export const xMergeWithCss = <TObject1, TObject2>(object1: TObject1, object2: TObject2): TObject1 & TObject2 => {
    return mergeWith({}, object1, object2, (destObj, srcObj) => {
        if (isArray(destObj)) {
            return destObj.concat(srcObj);
        }
    });
};

/**
 * Copy string and save it in clipboard
 * @param {String} text
 */
export const clipboardText = (text: string) => {
    if (typeof window === "undefined") {
        return;
    }

    const id = "mycustom-clipboard--hidden-id";
    let existsClipboardElement = document.getElementById(id);

    if (!existsClipboardElement) {
        const clipboardElement = document.createElement("textarea");
        clipboardElement.id = id;
        // Place in top-left corner of screen regardless of scroll position.
        clipboardElement.style.position = "fixed";
        clipboardElement.style.top = "-999px";
        clipboardElement.style.left = "-999px";

        // Ensure it has a small width and height. Setting to 1px / 1em
        // doesn't work as this gives a negative w/h on some browsers.
        clipboardElement.style.width = "1px";
        clipboardElement.style.height = "1px";

        // We don't need padding, reducing the size if it does flash render.
        clipboardElement.style.padding = "0px";

        // Clean up any borders.
        clipboardElement.style.border = "none";
        clipboardElement.style.outline = "none";
        clipboardElement.style.boxShadow = "none";

        // Avoid flash of white box if rendered for any reason.
        clipboardElement.style.background = "transparent";
        document.querySelector("body")!.appendChild(clipboardElement);

        existsClipboardElement = document.getElementById(id);
    }

    (existsClipboardElement as any)!.value = text;
    (existsClipboardElement as any)!.select();

    try {
        const status = document.execCommand("copy");
        if (!status) {
            // Cannot copy text
            (existsClipboardElement as any).parentNode.removeChild(existsClipboardElement);
            return false;
        }

        (existsClipboardElement as any).parentNode.removeChild(existsClipboardElement);
        // The text is now on the clipboard
        return true;
    } catch (err) {
        (existsClipboardElement as any).parentNode.removeChild(existsClipboardElement);
        // Unable to copy.
        return false;
    }
};
